/**
 * O tempo da previsão — funções puras (testadas em `previsao.test.ts`).
 *
 * - Cada passo vale num instante: o início da rodada + a antecedência.
 * - Temperatura, umidade, vento e MP2,5 são do INSTANTE do passo; chuva e
 *   rajada são do INTERVALO que termina nele (do passo anterior até ele).
 *   Para um instante qualquer: o passo mais próximo, ou o intervalo que o
 *   contém.
 * - Os dias da barra são os de Brasília (UTC−3, sem horário de verão desde
 *   2019). O primeiro é "hoje", só com as horas que faltam.
 */
import { BRT_OFFSET_MS, DAY_MS, HOUR_MS } from '../lib/time';
import { instanteDaRodada, type Modelo } from './api';

/** Instantes (epoch ms) dos passos de uma rodada. */
export function instantesDaRodada(m: Pick<Modelo, 'passos'>, rodada: string): number[] {
  const t0 = instanteDaRodada(rodada);
  return m.passos.map((h) => t0 + h * HOUR_MS);
}

/** Índice do passo mais próximo do instante (empate: o mais cedo); −1 sem passos. */
export function passoMaisProximo(instantes: readonly number[], t: number): number {
  let melhor = -1;
  let dist = Infinity;
  for (let k = 0; k < instantes.length; k++) {
    const d = Math.abs(instantes[k] - t);
    if (d < dist) {
      dist = d;
      melhor = k;
    }
  }
  return melhor;
}

/**
 * Índice do passo cujo intervalo (passo anterior, passo] contém o instante;
 * −1 antes do primeiro intervalo (o passo 0 não tem intervalo) ou depois do
 * último passo.
 */
export function passoDoIntervalo(instantes: readonly number[], t: number): number {
  for (let k = 1; k < instantes.length; k++) {
    if (t > instantes[k - 1] && t <= instantes[k]) return k;
  }
  return -1;
}

/** Duração (h) do intervalo que termina no passo k (0 no primeiro). */
export function horasDoIntervalo(passos: readonly number[], k: number): number {
  return k > 0 ? passos[k] - passos[k - 1] : 0;
}

/** Meia-noite (epoch ms, UTC) do dia de Brasília que contém o instante. */
export function inicioDoDiaBrt(t: number): number {
  return Math.floor((t + BRT_OFFSET_MS) / DAY_MS) * DAY_MS - BRT_OFFSET_MS;
}

const DIAS_SEMANA = ['dom', 'seg', 'ter', 'qua', 'qui', 'sex', 'sáb'];

export interface DiaDaBarra {
  /** Meia-noite de Brasília (epoch ms). */
  inicio: number;
  /** "sáb 17". */
  rotulo: string;
  /** Primeiro dia: só as horas que faltam. */
  hoje: boolean;
  /** 8º dia em diante: tendência, menos confiável. */
  tendencia: boolean;
  /** Índices dos passos do dia (a partir de `desde`). */
  passos: number[];
}

/** Rótulo curto de um dia de Brasília: "sáb 17". */
export function rotuloDoDia(inicio: number): string {
  const d = new Date(inicio + BRT_OFFSET_MS);
  return `${DIAS_SEMANA[d.getUTCDay()]} ${d.getUTCDate()}`;
}

/** A partir de qual dia (índice, 0 = hoje) a previsão é só tendência. */
export const DIA_TENDENCIA = 7;

/**
 * Dias de Brasília com passos a partir de `desde` (o instante atual): os
 * passos que já passaram não entram — a rodada começa horas antes de ser
 * publicada.
 */
export function diasDaBarra(instantes: readonly number[], desde: number): DiaDaBarra[] {
  const dias: DiaDaBarra[] = [];
  const hoje = inicioDoDiaBrt(desde);
  for (let k = 0; k < instantes.length; k++) {
    const t = instantes[k];
    if (t < desde - FOLGA_HOJE_MS) continue;
    const inicio = inicioDoDiaBrt(t);
    let dia = dias[dias.length - 1];
    if (!dia || dia.inicio !== inicio) {
      const n = Math.round((inicio - hoje) / DAY_MS);
      dia = { inicio, rotulo: rotuloDoDia(inicio), hoje: n === 0, tendencia: n >= DIA_TENDENCIA, passos: [] };
      dias.push(dia);
    }
    dia.passos.push(k);
  }
  return dias;
}

/**
 * A régua de um dia da barra: uma casa por intervalo do dia (8 com passos de
 * 3 h, 4 com os de 6 h), cada uma com o índice do passo dela ou null (já
 * passou, em "hoje", ou fora da rodada). Todos os dias têm a régua do dia
 * inteiro, para as réguas terem a mesma largura e se compararem.
 */
export function reguaDoDia(instantes: readonly number[], dia: Pick<DiaDaBarra, 'inicio' | 'passos'>): (number | null)[] {
  const ks = dia.passos;
  if (ks.length === 0) return [];
  let intervalo = Infinity;
  for (let i = 1; i < ks.length; i++) intervalo = Math.min(intervalo, instantes[ks[i]] - instantes[ks[i - 1]]);
  if (!Number.isFinite(intervalo)) {
    // Um passo só no dia: o intervalo é o do vizinho na rodada.
    const k = ks[0];
    const viz = k > 0 ? instantes[k] - instantes[k - 1] : k + 1 < instantes.length ? instantes[k + 1] - instantes[k] : 3 * HOUR_MS;
    intervalo = viz;
  }
  intervalo = Math.min(DAY_MS, Math.max(HOUR_MS, intervalo));
  const n = Math.max(1, Math.round(DAY_MS / intervalo));
  const casas: (number | null)[] = Array.from({ length: n }, () => null);
  for (const k of ks) {
    const i = Math.min(n - 1, Math.max(0, Math.floor((instantes[k] - dia.inicio) / intervalo)));
    if (casas[i] === null) casas[i] = k;
  }
  return casas;
}

/** Folga para "hoje": o passo de até 1 h 30 atrás ainda conta (é o mais próximo de agora). */
const FOLGA_HOJE_MS = 1.5 * HOUR_MS;

/** Primeiro passo que a barra mostra (os anteriores já passaram); 0 se todos passaram. */
export function primeiroPassoVisivel(instantes: readonly number[], agora: number): number {
  const k = instantes.findIndex((t) => t >= agora - FOLGA_HOJE_MS);
  return k < 0 ? Math.max(0, instantes.length - 1) : k;
}

/** Primeiro passo a mostrar ao entrar no modo: o mais próximo de agora (nunca antes do primeiro). */
export function passoInicial(instantes: readonly number[], agora: number): number {
  return passoMaisProximo(instantes, agora);
}

/** Passo do dia mais perto do meio-dia (tocar num dia leva a ele). */
export function passoDoMeioDia(instantes: readonly number[], dia: Pick<DiaDaBarra, 'inicio' | 'passos'>): number {
  const alvo = dia.inicio + 12 * HOUR_MS;
  let melhor = dia.passos[0] ?? -1;
  for (const k of dia.passos) if (Math.abs(instantes[k] - alvo) < Math.abs(instantes[melhor] - alvo)) melhor = k;
  return melhor;
}

/** "sáb 17/10 18:00" (Brasília) — o instante previsto em todo rótulo do modo. */
export function rotuloDoInstante(t: number): string {
  const d = new Date(t + BRT_OFFSET_MS);
  const p = (n: number) => String(n).padStart(2, '0');
  return `${DIAS_SEMANA[d.getUTCDay()]} ${p(d.getUTCDate())}/${p(d.getUTCMonth() + 1)} ${p(d.getUTCHours())}:${p(d.getUTCMinutes())}`;
}
